import { defineStore } from 'pinia';
import { useCompanyStore } from '@/services/UserService/useCompanyStore';
import { useMeStore } from '@/services/UserService/useMeStore';
import { getEntryTimeDates, createDateFromLocalTime } from '@/utils/dateTimeUtils';
export const useEntryInitStore = defineStore('useEntryInitStore', {
  state: () => {
    const {
      companySettings,
      loadMyCompany
    } = useCompanyStore();
    loadMyCompany();
    return {
      companySettings,
      localStep: 0.1,
      form: {
        client: {},
        matter: {},
        codes: {
          activity: {
            id: null
          },
          task: {
            id: null
          }
        },
        date: null,
        startTime: null,
        endTime: null
      }
    };
  },
  actions: {
    roundedTime(t) {
      t = new Date(t); // make sure it is a date object
      let minutes = t.getMinutes();
      if (minutes === 0 || minutes === 30) {
        this.updateStep(0.5);
      } else if (minutes === 15 || minutes === 45) {
        this.updateStep(0.25);
      } else if (minutes % 10 === 0) {
        this.updateStep(0.1);
      } else {
        this.companySettings.timeEntryIncrement == '0.1' ? this.updateStep(0.1) : this.updateStep(0.01);
        t.setMinutes(Math.floor(t.getMinutes() / 6) * 6);
      }
      t.setSeconds(0);
      return t;
    },
    updateStep(hours) {
      if (this.companySettings.enforceRounding || this.mod(hours, this.companySettings.timeEntryIncrement) === 0) {
        this.localStep = this.companySettings.timeEntryIncrement;
      } else if (this.mod(hours, 0.5) === 0) {
        this.localStep = 0.5;
      } else if (this.mod(hours, 0.25) === 0) {
        this.localStep = 0.25;
      } else if (this.mod(hours, 0.1) === 0) {
        this.localStep = 0.1;
      } else {
        this.localStep = 0.01;
      }
    },
    mod(a, b) {
      // This is necessary to ensure that e.g. mod(0.5,0.1) returns 0.
      // Build in operator result in rounding errors, e.g. 0.5 % 0.1 = 0.99999999999..
      return Math.round(a * 100) % Math.round(b * 100);
    },
    updateEndTime(form, newHours) {
      if (!form.startTime) return;
      const [hours, minutes] = form.startTime.split(':').map(Number);
      let totalMinutes = hours * 60 + minutes + newHours * 60;
      const newHours24 = Math.floor(totalMinutes / 60) % 24;
      const newMinutes = Math.floor(totalMinutes % 60);
      form.endTime = `${String(newHours24).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
      return form.endTime;
    },
    newDefaultEntry(init, item) {
      const entry = JSON.parse(JSON.stringify(init)) || {};
      const roundedTime = this.roundedTime(entry.startTime || new Date());
      entry.startTime = roundedTime, entry.date = new Date(entry.startTime);
      if (item) {
        entry.item = item;
      }
      entry.matter = entry.matter || {
        label: 'Select Matter',
        id: ''
      };
      entry.client = entry.matter?.client || entry.client || {
        label: 'Select Client',
        id: ''
      };
      entry.codes = entry.codes || {
        activity: {
          id: null
        },
        task: {
          id: null
        }
      };
      entry.description = entry.description || '';
      entry.rate = entry.rate || {};
      entry.hours = entry.hours || this.companySettings.timeEntryIncrement || 0.1;
      entry.billingType = entry.billingType ? entry.billingType : undefined;
      return entry;
    },
    loadEntry(entry) {
      // Map rateEdited from backend to rate.edited for frontend
      const rate = entry.rate || {};
      if (entry.rateEdited === true) {
        rate.edited = true;
      }

      // Story #41530: Use shared utility for localDateTime support
      const meStore = useMeStore();
      const useDateHandling = meStore.features?.dateHandling;
      const {
        startTime,
        endTime
      } = getEntryTimeDates(entry, useDateHandling);

      // Use localDateTime.date when dateHandling is enabled to avoid UTC midnight
      // shift that new Date("YYYY-MM-DD") causes in non-UTC timezones.
      const entryDate = useDateHandling && entry.localDateTime?.date ? createDateFromLocalTime(entry.localDateTime.date, entry.localDateTime.time) : new Date(entry.date);
      return {
        ...entry,
        client: {
          ...entry.matter?.client
        },
        codes: {
          activity: {
            ...entry?.codes?.activity
          },
          task: {
            ...entry?.codes?.task
          }
        },
        date: entryDate,
        startTime,
        endTime,
        rate,
        charge: entry.charge ? {
          description: entry.charge.label || entry.charge.description,
          displayName: entry.charge.displayName || entry.charge.label || entry.charge.description,
          id: entry.charge.id
        } : undefined,
        loading: false
      };
    },
    loadEntryFromExternalEvents(entry) {
      const entryDate = new Date(entry.date + 'T00:00:00');
      return {
        description: entry.subject,
        hours: entry.hours,
        date: entryDate,
        startTime: this.convert12HourTimeToSpecificDate(entry.startTime, entryDate),
        endTime: this.convert12HourTimeToSpecificDate(entry.endTime, entryDate),
        externalEventId: entry.externalId,
        loading: false,
        externalEventProviderType: 'm365Calendar'
      };
    },
    convert12HourTimeToSpecificDate(timeString, baseDate) {
      const [time, period] = timeString.split(' ');
      const [hours, minutes] = time.split(':').map(Number);
      let hours24 = hours;
      if (period === 'PM' && hours !== 12) {
        hours24 += 12;
      } else if (period === 'AM' && hours === 12) {
        hours24 = 0;
      }
      const result = new Date(baseDate);
      result.setHours(hours24, minutes, 0, 0);
      return result;
    }
  }
});